import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { API_BASE_URL } from '@/config/api';
import { Loader2, Settings, Menu, X, LogOut, Cog, Archive } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AnalysisList } from '@/components/AnalysisList';
import { ReportViewer } from '@/components/ReportViewer';
import { ConsultationDialog } from '@/components/ConsultationDialog';
import { ConfigurationDrawer } from '@/components/ConfigurationDrawer';
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarProvider } from '@/components/ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useIsMobile } from '@/hooks/use-mobile';
import { useConsultationFlow } from '@/hooks/useConsultationFlow';
import { useConsultationTranscription } from '@/hooks/useConsultationTranscription';
import CountdownOverlay from '@/components/CountdownOverlay';
import { useToast } from '@/hooks/use-toast';
import ModalForceFinish from '@/components/ModalForceFinish';
import RecordingInProgressModal from '@/components/RecordingInProgressModal';
import { useRecordingIntervalModal } from '@/hooks/useRecordingIntervalModal';
import { useTranslation } from 'react-i18next';
import Joyride from 'react-joyride';
import { STATUS } from 'react-joyride';
import SlowConnectionModal from '@/components/SlowConnectionModal';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { FileUploadModal } from '@/components/FileUploadModal';

interface Analysis {
  id: number;
  attributes: {
    patient_name: string;
    createdAt: string;
    template: {
      data: {
        attributes: {
          specialty: string;
          sections: any[];
        };
      };
    };
    transcription?: any[];
    iaReport?: Record<string, any>;
  };
}

interface Template {
  id: number;
  attributes: {
    specialty: string;
    sections: any[];
  };
}

interface AnalysesResponse {
  data: Analysis[];
  meta: {
    pagination: {
      page: number;
      pageCount: number;
      pageSize: number;
      total: number;
    };
  };
}

const Dashboard = () => {
  const {
    user,
    logout,
    getUser
  } = useAuth();
  const {
    analysisId
  } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  // Add network status monitoring
  const {
    isOnline,
    isSlowConnection,
    showSlowConnectionModal,
    dismissSlowConnectionModal
  } = useNetworkStatus();

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileAnalysisMenuOpen, setMobileAnalysisMenuOpen] = useState(false);
  const [configurationDrawerOpen, setConfigurationDrawerOpen] = useState(false);
  const [userTemplate, setUserTemplate] = useState<Template | null>(null);
  const [pendingAnalysisId, setPendingAnalysisId] = useState<number | null>(null);
  const [isCreatingAnalysis, setIsCreatingAnalysis] = useState(false);
  const [persistentTranscriptions, setPersistentTranscriptions] = useState<any[]>([]);
  const [isInContinuation, setIsInContinuation] = useState(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [tourVisible, setTourVisible] = useState(false);
  const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(false);
  const [loadingStartTime, setLoadingStartTime] = useState<number | null>(null);
  const [showArchivedAnalyses, setShowArchivedAnalyses] = useState(false);
  const [isCreatingAndNavigating, setIsCreatingAndNavigating] = useState(false);
  const [justCreatedAnalysisId, setJustCreatedAnalysisId] = useState<number | null>(null);
  const [hasNavigated, setHasNavigated] = useState(false); // Nuevo estado para controlar si ya navegamos
  const [isFileUploadLoading, setIsFileUploadLoading] = useState(false); // New state for file upload loading
  const isMobile = useIsMobile();
  const [modalRecordingInProcess, setModalRecordingInProcess] = useState(false);

  // Add the missing toggleArchivedAnalyses function
  const toggleArchivedAnalyses = () => {
    setShowArchivedAnalyses(!showArchivedAnalyses);
  };

  // Add the missing handleMobileAnalysisMenuClose function
  const handleMobileAnalysisMenuClose = () => {
    setMobileAnalysisMenuOpen(false);
  };

  // Fetch analyses list with infinite query
  const {
    data: analysesData,
    isLoading: analysesLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery({
    queryKey: ['analyses', showArchivedAnalyses],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await axios.get(`${API_BASE_URL}/analyses`, {
        params: {
          locale: 'all',
          'populate': '*,template.sections',
          'filters[doctor]': user?.id,
          'filters[archived]': showArchivedAnalyses,
          'sort[]': 'createdAt:desc',
          'pagination[page]': pageParam,
          'pagination[pageSize]': 25
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data as AnalysesResponse;
    },
    getNextPageParam: (lastPage: AnalysesResponse) => {
      const { page, pageCount } = lastPage.meta.pagination;
      return page < pageCount ? page + 1 : undefined;
    },
    initialPageParam: 1,
    enabled: !!user?.id,
    staleTime: 30 * 1000, // Reduce stale time to 30 seconds for more frequent updates
    gcTime: 2 * 60 * 1000, // Reduce cache time to 2 minutes
    refetchOnWindowFocus: true, // Enable refetch on window focus
  });

  // Flatten analyses data from all pages
  const allAnalyses = analysesData?.pages.flatMap(page => page.data) || [];

  // Fetch specific analysis when analysisId is present
  const {
    data: selectedAnalysis,
    isLoading: analysisLoading
  } = useQuery({
    queryKey: ['analysis', analysisId],
    queryFn: async () => {
      const response = await axios.get(`${API_BASE_URL}/analyses/${analysisId}`, {
        params: {
          locale: 'all',
          'populate': '*,template.sections'
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data;
    },
    enabled: !!analysisId
  });

  // Load user's default template
  const {
    data: templateData,
    isLoading: templateLoading,
    error: templateError
  } = useQuery({
    queryKey: ['userTemplate', user?.id],
    queryFn: async () => {
      const response = await axios.get(`${API_BASE_URL}/templates`, {
        params: {
          locale: 'all',
          'filters[user]': user?.id,
          'populate': '*'
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Handle template data when it's loaded
  useEffect(() => {
    if (templateData?.data && templateData.data[0]) {
      setUserTemplate({
        id: templateData.data[0].id,
        attributes: templateData.data[0].attributes
      });
      console.log('User template loaded:', templateData.data[0]);
    } else if (templateData && !templateLoading && (!templateData.data || templateData.data.length === 0)) {
      // User has no template, redirect to onboarding
      console.log('No template found, redirecting to onboarding');
      navigate('/onboarding');
    }
  }, [templateData, templateLoading, navigate]);

  // Also check if there's a template error and redirect
  useEffect(() => {
    if (templateError && !templateLoading) {
      console.log('Template error, redirecting to onboarding:', templateError);
      navigate('/onboarding');
    }
  }, [templateError, templateLoading, navigate]);

  // Add handler for microphone not found that directly triggers the dialog
  const handleMicrophoneNotFoundError = () => {
    console.log('🎤 [DASHBOARD] Microphone not found - triggering error dialog');
    handleMicrophoneNotFound();
  };

  // Move useConsultationFlow before useConsultationTranscription and pass userTemplate
  const {
    showContinueButton,
    consultationDialogOpen,
    showCountdown,
    isRecording,
    countdownKey,
    isContinuation,
    isNewConsultation,
    shouldCreateAnalysis,
    microphoneErrorOpen,
    fileUploadModalOpen,
    setConsultationDialogOpen,
    setMicrophoneErrorOpen,
    setFileUploadModalOpen,
    handleStartRecording,
    handleCountdownComplete: handleCountdownCompleteOriginal,
    handleStopRecording,
    handleContinueConsultation: handleContinueConsultationOriginal,
    handleFinishConsultation,
    handleUploadFile,
    handleFileTranscriptionComplete,
    handleMicrophoneNotFound,
    handleRetryMicrophone
  } = useConsultationFlow({
    onMobileMenuClose: handleMobileAnalysisMenuClose,
    template: userTemplate,
    onMicrophoneNotFound: handleMicrophoneNotFoundError,
    currentAnalysisId: analysisId ? parseInt(analysisId) : undefined
  });

  // Create the missing createAnalysisAndNavigate function
  const createAnalysisAndNavigate = async () => {
    if (!userTemplate || !userTemplate.attributes || !user) {
      toast({
        title: t('error'),
        description: t('template_load_error'),
        variant: "destructive",
      });
      return null;
    }

    try {
      console.log('Creating analysis and navigating...');
      setIsCreatingAnalysis(true);
      setIsCreatingAndNavigating(true);

      const iaReport = Object.fromEntries(
        userTemplate.attributes.sections.map((section) => [section.key, "registrar"])
      );

      const response = await axios.post(`${API_BASE_URL}/analyses`, {
        data: {
          name: t('new_analysis'),
          doctor: user.id,
          template: userTemplate.id,
          iaReport: iaReport,
          transcription: [],
          diffs: [],
          archived: false,
          start: new Date().toISOString(),
          email: user.email,
          notes: [],
        },
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      const newAnalysisId = response.data.data.id;
      console.log('Analysis created with ID:', newAnalysisId);

      // Mark this as a just created analysis to handle navigation differently
      setJustCreatedAnalysisId(newAnalysisId);

      // Create the complete analysis object
      const newAnalysis = {
        id: newAnalysisId,
        attributes: {
          patient_name: t('new_analysis'),
          subject: null,
          createdAt: new Date().toISOString(),
          start: new Date().toISOString(),
          finished: false,
          archived: false,
          iaReport: iaReport,
          transcription: [],
          diffs: [],
          template: {
            data: {
              id: userTemplate.id,
              attributes: {
                specialty: userTemplate.attributes.specialty,
                sections: userTemplate.attributes.sections
              }
            }
          }
        }
      };

      // Update the analyses list cache - add to the beginning of the first page
      queryClient.setQueryData(['analyses', showArchivedAnalyses], (oldData: any) => {
        if (!oldData) {
          return {
            pages: [{
              data: [newAnalysis],
              meta: {
                pagination: {
                  page: 1,
                  pageCount: 1,
                  pageSize: 25,
                  total: 1
                }
              }
            }],
            pageParams: [1]
          };
        }
        
        const newData = { ...oldData };
        if (newData.pages && newData.pages[0]) {
          newData.pages[0] = {
            ...newData.pages[0],
            data: [newAnalysis, ...newData.pages[0].data],
            meta: {
              ...newData.pages[0].meta,
              pagination: {
                ...newData.pages[0].meta.pagination,
                total: newData.pages[0].meta.pagination.total + 1
              }
            }
          };
        }
        return newData;
      });

      // Pre-populate the individual analysis cache
      queryClient.setQueryData(['analysis', newAnalysisId.toString()], {
        data: newAnalysis
      });

      // Invalidate to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['analyses'] });

      console.log('Navigating to new analysis:', newAnalysisId);
      
      // Navigate to the new analysis
      navigate(`/dashboard/${newAnalysisId}`);
      
      // Close mobile menu if open
      if (isMobile) {
        setMobileAnalysisMenuOpen(false);
      }

      return newAnalysisId;

    } catch (error) {
      console.error('Error creating analysis:', error);
      setIsCreatingAnalysis(false);
      setIsCreatingAndNavigating(false);

      toast({
        title: t('error'),
        description: 'No se pudo crear el análisis',
        variant: "destructive",
      });
      return null;
    }
  };

  // Use the recording interval modal hook
  const {
    showModal: showIntervalModal,
    handleContinue,
    handleFinalize
  } = useRecordingIntervalModal({
    isRecording,
    onForceFinish: () => {
      handleFinishConsultationWithPDF();
    },
    intervalMinutes: 15
  });

  // Handle time updates from timer
  const handleTimeUpdate = useCallback((formattedTime: string, totalSeconds: number) => {
    setCurrentTime(totalSeconds);
  }, []);

  // Now we can use isRecording and isContinuation in useConsultationTranscription
  const { 
    transcriptions: liveTranscriptions, 
    loadExistingTranscriptions 
  } = useConsultationTranscription({
    analysisId: analysisId ? parseInt(analysisId) : undefined,
    isRecording: isRecording,
    isContinuation: isContinuation,
    currentTime: currentTime,
    shouldCreateAnalysis: shouldCreateAnalysis,
    onTranscriptionUpdate: (newTranscriptions) => {
      if (analysisId && parseInt(analysisId) === selectedAnalysis?.data?.id) {
        setPersistentTranscriptions(newTranscriptions);
      }
    },
    onMicrophoneNotFound: handleMicrophoneNotFoundError,
    onMicrophoneVerified: createAnalysisAndNavigate
  });

  // NUEVO: Efecto para cargar transcripciones existentes cuando cambia el análisis seleccionado
  useEffect(() => {
    if (selectedAnalysis && selectedAnalysis.data.attributes.transcription && selectedAnalysis.data.attributes.transcription.length > 0) {
      console.log('📥 DASHBOARD: Loading existing transcriptions for analysis:', selectedAnalysis.data.id);
      console.log('📥 DASHBOARD: Found', selectedAnalysis.data.attributes.transcription.length, 'existing transcriptions');
      
      // Cargar transcripciones existentes SIN re-procesarlas
      loadExistingTranscriptions(selectedAnalysis.data.attributes.transcription);
    } else if (selectedAnalysis) {
      console.log('📥 DASHBOARD: No existing transcriptions found for analysis:', selectedAnalysis.data.id);
      // Si no hay transcripciones existentes, limpiar
      loadExistingTranscriptions([]);
    }
  }, [selectedAnalysis, loadExistingTranscriptions]);

  // Combine saved transcriptions with persistent ones - MODIFICADO para evitar duplicación
  const combinedTranscriptions = selectedAnalysis ? [
    ...persistentTranscriptions // Solo usar las transcripciones persistentes, ya incluyen las guardadas
  ] : [];

  // Enhanced consultation flow with analysis creation
  const handleStartRecordingWithAnalysis = async () => {
    // Add null check for userTemplate and user
    if (!userTemplate || !userTemplate.attributes || !user) {
      toast({
        title: t('error'),
        description: t('template_load_error'),
        variant: "destructive",
      });
      return;
    }

    console.log('🚀 [DASHBOARD] Starting NEW consultation - will create analysis');
    refreshAnalyses();
    handleStartRecording();
  };

  // Handle countdown complete - NO longer create analysis here
  const handleCountdownComplete = async () => {
    console.log('🚀 [DASHBOARD] Countdown complete - now attempting to start recording and verify microphone');
    handleCountdownCompleteOriginal();
  };

  // Enhanced continue consultation handler
  const handleContinueConsultation = () => {
    console.log('🔄 [DASHBOARD] CONTINUING consultation - will NOT create analysis');
    console.log('🔄 [DASHBOARD] Current analysis ID:', analysisId);
    setIsInContinuation(true);
    handleContinueConsultationOriginal();
    // Reset continuation flag after a short delay
    setTimeout(() => setIsInContinuation(false), 1000);
  };

  // Enhanced finish consultation handler - ENSURE finished flag is set to true
  const handleFinishConsultationWithPDF = async () => {
    console.log('Finishing consultation and uploading PDF to Strapi (no download)');

    // First, finish the consultation to stop recording
    handleFinishConsultation();

    // Generate and upload PDF if we have an analysis - SOLO SUBIR
    if (selectedAnalysis) {
      try {
        // Import jsPDF
        const jsPDF = (await import('jspdf')).default;

        // Create new PDF document
        const pdf = new jsPDF();
        const pageWidth = pdf.internal.pageSize.getWidth();
        const margin = 20;
        const maxWidth = pageWidth - (margin * 2);
        let yPosition = margin;

        // Add title
        pdf.setFontSize(18);
        pdf.setFont(undefined, 'bold');
        const title = selectedAnalysis.data.attributes.patient_name || 'Reporte Médico';
        pdf.text(title, margin, yPosition);
        yPosition += 15;

        // Add date
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'normal');
        const date = new Date(selectedAnalysis.data.attributes.createdAt).toLocaleDateString('es-ES');
        pdf.text(`Fecha: ${date}`, margin, yPosition);
        yPosition += 20;

        // Get sections from template that should be included in report
        const reportSections = selectedAnalysis.data.attributes.template?.data?.attributes?.sections?.filter(
          section => section.includeInReport
        ) || [];

        // Add each section
        reportSections.forEach((section) => {
          const sectionContent = selectedAnalysis.data.attributes.iaReport?.[section.key] || '';

          if (sectionContent && sectionContent !== 'registrar') {
            // Add section title
            pdf.setFontSize(14);
            pdf.setFont(undefined, 'bold');

            // Check if we need a new page
            if (yPosition > pdf.internal.pageSize.getHeight() - 40) {
              pdf.addPage();
              yPosition = margin;
            }

            pdf.text(section.title, margin, yPosition);
            yPosition += 10;

            // Add section content
            pdf.setFontSize(10);
            pdf.setFont(undefined, 'normal');

            // Clean the content (remove HTML tags if any)
            const cleanContent = sectionContent.replace(/<[^>]*>/g, '').trim();

            // Split content into lines that fit the page width
            const lines = pdf.splitTextToSize(cleanContent, maxWidth);

            lines.forEach((line: string) => {
              // Check if we need a new page
              if (yPosition > pdf.internal.pageSize.getHeight() - 20) {
                pdf.addPage();
                yPosition = margin;
              }

              pdf.text(line, margin, yPosition);
              yPosition += 6;
            });

            yPosition += 10; // Extra space between sections
          }
        });

        // Add transcription section if there are transcriptions
        if (combinedTranscriptions.length > 0) {
          // Check if we need a new page for transcription
          if (yPosition > pdf.internal.pageSize.getHeight() - 60) {
            pdf.addPage();
            yPosition = margin;
          } else {
            yPosition += 10; // Extra space before transcription
          }

          // Add transcription title
          pdf.setFontSize(14);
          pdf.setFont(undefined, 'bold');
          pdf.text('Transcripción de la Consulta', margin, yPosition);
          yPosition += 15;

          // Add each transcription entry
          combinedTranscriptions.forEach((transcription) => {
            // Check if we need a new page
            if (yPosition > pdf.internal.pageSize.getHeight() - 30) {
              pdf.addPage();
              yPosition = margin;
            }

            // Add speaker and time
            pdf.setFontSize(9);
            pdf.setFont(undefined, 'bold');
            const speakerLine = `${transcription.speakerType} (${transcription.recordTime}):`;
            pdf.text(speakerLine, margin, yPosition);
            yPosition += 8;

            // Add transcription text
            pdf.setFontSize(9);
            pdf.setFont(undefined, 'normal');
            const transcriptLines = pdf.splitTextToSize(transcription.transcript, maxWidth);

            transcriptLines.forEach((line: string) => {
              // Check if we need a new page
              if (yPosition > pdf.internal.pageSize.getHeight() - 15) {
                pdf.addPage();
                yPosition = margin;
              }

              pdf.text(line, margin, yPosition);
              yPosition += 5;
            });

            yPosition += 8; // Space between transcription entries
          });
        }

        // Generate filename
        const filename = `reporte_${selectedAnalysis.data.attributes.patient_name || 'medico'}_${date.replace(/\//g, '-')}.pdf`;

        // Get PDF as blob for upload to Strapi
        const pdfBlob = pdf.output('blob');

        // Show loading toast
        toast({
          title: t('generating_pdf'),
          description: 'Subiendo el reporte a Strapi...',
        });

        // Upload to Strapi - SOLO SUBIR, NO DESCARGAR
        const { uploadFileToStrapi } = await import('@/utils/fileUpload');
        const uploadedFileId = await uploadFileToStrapi(pdfBlob, filename);

        if (uploadedFileId) {
          // Update the analysis with the PDF ID, finished = true and end date
          try {
            const endDate = new Date().toISOString();
            
            // Update analysis using axios directly since the type issue is with updateAnalysisFile
            await axios.put(`${API_BASE_URL}/analyses/${selectedAnalysis.data.id}`, {
              data: { 
                pdf: uploadedFileId,
                finished: true,
                end: endDate
              }
            }, {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json',
              }
            });

            // Update local state immediately to reflect changes in UI
            const updatedAnalysisData = {
              ...selectedAnalysis.data,
              attributes: {
                ...selectedAnalysis.data.attributes,
                finished: true,
                end: endDate,
                pdf: uploadedFileId
              }
            };

            // Update the query cache to reflect changes immediately
            queryClient.setQueryData(['analysis', analysisId], {
              data: updatedAnalysisData
            });

            toast({
              title: t('pdf_uploaded_successfully'),
              description: t('pdf_uploaded_successfully_description'),
            });

            console.log('PDF uploaded to Strapi with ID:', uploadedFileId, 'and analysis marked as finished with end date:', endDate);
          } catch (updateError) {
            console.error('Error updating analysis with PDF:', updateError);
            toast({
              title: t('pdf_uploaded_warning'),
              description: t('pdf_uploaded_warning_description'),
              variant: 'destructive'
            });
          }
        } else {
          toast({
            title: t('pdf_upload_error'),
            description: t('pdf_upload_error_description'),
            variant: 'destructive'
          });
        }

      } catch (error) {
        console.error('Error generating/uploading PDF:', error);
        toast({
          title: t('error'),
          description: t('pdf_generation_error'),
          variant: 'destructive'
        });
      }
    }
  };

  // Reset persistent transcriptions when changing analysis or finishing consultation
  useEffect(() => {
    // Remove the automatic clearing when consultation finishes
    // Transcriptions will now remain visible after finishing consultation
    // They will only be cleared when explicitly changing analysis or starting new consultation
  }, []);

  // Reset persistent transcriptions when changing to a different analysis
  useEffect(() => {
    if (analysisId) {
      const currentId = parseInt(analysisId);
      if (selectedAnalysis?.data?.id !== currentId) {
        setPersistentTranscriptions([]);
      }
    }
  }, [analysisId, selectedAnalysis?.data?.id]);

  // Enhanced clearing logic - don't clear during continuation
  useEffect(() => {
    if (isRecording) {
      // Only clear if we're starting a new recording and NOT in continuation mode
      const currentAnalysisId = analysisId ? parseInt(analysisId) : null;
      if (currentAnalysisId && selectedAnalysis?.data?.id === currentAnalysisId) {
        // Only clear transcriptions if this is NOT a continuation AND not already in continuation state
        if (!isContinuation && !isInContinuation) {
          console.log('Starting new recording on current analysis - clearing previous transcriptions');
          setPersistentTranscriptions([]);
        } else {
          console.log('Continuing recording - preserving existing transcriptions');
        }
      }
    }
  }, [isRecording, analysisId, selectedAnalysis?.data?.id, isContinuation, isInContinuation]);

  // Modified analysis selection to check for recording in progress and optimize loading
  const handleAnalysisSelect = async (id: number) => {
    // Special handling for just created analysis - don't check for recording
    if (justCreatedAnalysisId === id) {
      console.log('Selecting just created analysis, bypassing recording check');
      setJustCreatedAnalysisId(null); // Reset the flag
      
      // Set loading state immediately for just created analysis
      setIsLoadingAnalysis(true);
      setLoadingStartTime(Date.now());
      
      // Navigate immediately
      navigate(`/dashboard/${id}`);
      
      if (isMobile) {
        setMobileAnalysisMenuOpen(false);
      }
      return;
    }

    if (isRecording) {
      setModalRecordingInProcess(true);
      return;
    }

    // Check if analysis already exists in cache
    const existingAnalysis = queryClient.getQueryData(['analysis', id.toString()]);
    
    if (existingAnalysis) {
      // Analysis exists in cache, navigate immediately without artificial delay
      console.log('Analysis exists in cache, navigating immediately');
      navigate(`/dashboard/${id}`);
      
      if (isMobile) {
        setMobileAnalysisMenuOpen(false);
      }
      return;
    }

    // Show loading state for unknown analysis
    setIsLoadingAnalysis(true);
    setLoadingStartTime(Date.now());

    // Navigate immediately
    navigate(`/dashboard/${id}`);

    // Close mobile analysis menu when selecting an analysis
    if (isMobile) {
      setMobileAnalysisMenuOpen(false);
    }
  };

  // Clear the creating states when analysis loads successfully or when navigation changes
  useEffect(() => {
    if (selectedAnalysis && !analysisLoading) {
      // Clear creating states when analysis loads
      setTimeout(() => {
        setIsCreatingAnalysis(false);
        setIsCreatingAndNavigating(false);
      }, 100);
    }
  }, [selectedAnalysis, analysisLoading]);

  // Also clear creating states when analysisId changes
  useEffect(() => {
    if (analysisId && justCreatedAnalysisId && parseInt(analysisId) === justCreatedAnalysisId) {
      // The navigation completed successfully
      setTimeout(() => {
        setIsCreatingAnalysis(false);
        setIsCreatingAndNavigating(false);
      }, 100);
    }
  }, [analysisId, justCreatedAnalysisId]);

  // Reset loading state when analysis is loaded - reduced minimum delay
  useEffect(() => {
    const handleLoadingEnd = async () => {
      if (selectedAnalysis && !analysisLoading && loadingStartTime) {
        const elapsedTime = Date.now() - loadingStartTime;
        const minimumLoadingTime = 800; // Reduced from 2 seconds to 800ms

        if (elapsedTime < minimumLoadingTime) {
          const remainingTime = minimumLoadingTime - elapsedTime;
          setTimeout(() => {
            setIsLoadingAnalysis(false);
            setLoadingStartTime(null);
          }, remainingTime);
        } else {
          setIsLoadingAnalysis(false);
          setLoadingStartTime(null);
        }
      }
    };

    handleLoadingEnd();
  }, [selectedAnalysis, analysisLoading, loadingStartTime]);

  // Also reset loading when there's an error or the analysis changes
  useEffect(() => {
    if (analysisId && loadingStartTime) {
      const timeoutId = setTimeout(() => {
        const elapsedTime = Date.now() - loadingStartTime;
        const minimumLoadingTime = 800; // Reduced here too

        if (elapsedTime >= minimumLoadingTime) {
          setIsLoadingAnalysis(false);
          setLoadingStartTime(null);
        }
      }, 3000); // Reduced timeout from 5 to 3 seconds

      return () => clearTimeout(timeoutId);
    }
  }, [analysisId, loadingStartTime]);

  const handleNewAnalysis = () => {
    // TODO: Implement new analysis creation
    console.log('New analysis clicked');
  };

  const handleRenameAnalysis = (id: number) => {
    // TODO: Implement rename analysis
    console.log('Rename analysis:', id);
  };

  const handleDeleteAnalysis = (id: number) => {
    // TODO: Implement delete analysis
    console.log('Delete analysis:', id);
  };

  const handleArchiveAnalysis = async (id: number) => {
    try {
      await axios.put(`${API_BASE_URL}/analyses/${id}`, {
        data: {
          archived: true
        }
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        }
      });

      toast({
        title: t('success'),
        description: t('analysis_archived_successfully'),
      });

      // Invalidate all analyses queries to refresh both active and archived lists
      await queryClient.invalidateQueries({ queryKey: ['analyses'] });
      
      // Also invalidate the specific analysis
      await queryClient.invalidateQueries({ queryKey: ['analysis', id.toString()] });
      
    } catch (error) {
      console.error('Error archiving analysis:', error);
      toast({
        title: t('error'),
        description: t('error_archiving_analysis'),
        variant: 'destructive'
      });
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleMobileAnalysisMenuToggle = () => {
    setMobileAnalysisMenuOpen(!mobileAnalysisMenuOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleConfiguration = () => {
    setConfigurationDrawerOpen(true);
  };

  // Add refresh function for analyses
  const refreshAnalyses = () => {
    queryClient.invalidateQueries({ queryKey: ['analyses'] });
  };

  // Enhanced file transcription complete handler with proper synchronization and extended loading
  const handleFileTranscriptionCompleteWithRefresh = async (transcriptions: any[], title: string, analysisId: number) => {
    console.log('File transcription complete - starting synchronized loading and navigation');
    
    // Set loading state immediately
    setIsFileUploadLoading(true);
    
    try {
      // Call the original handler first
      handleFileTranscriptionComplete(transcriptions, title, analysisId);
      
      // Navigate to the new analysis immediately
      navigate(`/dashboard/${analysisId}`);
      
      // Close mobile menu if open
      if (isMobile) {
        setMobileAnalysisMenuOpen(false);
      }
      
      console.log('Starting synchronized refresh and waiting for selection...');
      
      // Invalidate and wait for refetch to complete
      await queryClient.invalidateQueries({ queryKey: ['analyses'] });
      await queryClient.refetchQueries({ queryKey: ['analyses'] });
      
      // Also ensure the specific analysis is loaded
      await queryClient.refetchQueries({ queryKey: ['analysis', analysisId.toString()] });
      
      // Wait a bit more to ensure the UI has time to update the selection
      await new Promise(resolve => setTimeout(resolve, 500));
      
      console.log('Analysis list and specific analysis refreshed, selection should be synchronized');
      
    } catch (error) {
      console.error('Error refreshing analyses:', error);
    } finally {
      // Clear loading state
      setIsFileUploadLoading(false);
    }
  };

  // Handle tour functionality
  const handleJoyrideCallback = async (data: any) => {
    const { status } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setTourVisible(false);
      await saveTourState();
      getUser();
    }
  };

  const saveTourState = async () => {
    try {
      await axios.put(`${API_BASE_URL}/users/${user?.id}`, {
        tour: false
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        }
      });
    } catch (error) {
      console.error('Error saving tour state:', error);
    }
  };

  // Check if tour should be visible when user data changes
  useEffect(() => {
    if (user?.tour && analysisId) {
      setTourVisible(true);
    }
  }, [user?.tour, analysisId]);

  // ARREGLO: Efecto mejorado para navegación automática que evita múltiples creaciones
  useEffect(() => {
    // Solo ejecutar la navegación automática si:
    // 1. No hay analysisId en la URL (estamos en /dashboard)
    // 2. Tenemos usuario y template
    // 3. Los análisis terminaron de cargar (no están en loading)
    // 4. Aún no hemos navegado (evita múltiples ejecuciones)
    if (!analysisId && user && userTemplate && !analysesLoading && !hasNavigated) {
      console.log('Evaluando navegación automática...');
      
      if (allAnalyses.length === 0) {
        // No hay análisis, crear uno
        console.log('No analyses found, creating one automatically...');
        setHasNavigated(true); // Marcar que ya navegamos
        createAnalysisAndNavigate();
      } else {
        // Hay análisis, navegar al más reciente
        const mostRecentAnalysis = allAnalyses[0];
        console.log('Navigating to most recent analysis:', mostRecentAnalysis.id);
        setHasNavigated(true); // Marcar que ya navegamos
        navigate(`/dashboard/${mostRecentAnalysis.id}`, { replace: true });
      }
    }
  }, [analysisId, user, userTemplate, analysesLoading, allAnalyses, hasNavigated, navigate]);

  // Reset hasNavigated cuando el analysisId cambie (para permitir nueva navegación automática si se va a /dashboard)
  useEffect(() => {
    if (analysisId) {
      setHasNavigated(true); // Estamos en un análisis específico
    } else {
      setHasNavigated(false); // Estamos en /dashboard, permitir navegación automática
    }
  }, [analysisId]);

  return (
    <>
      {/* Network Status Modal */}
      <SlowConnectionModal
        isOpen={showSlowConnectionModal}
        onClose={dismissSlowConnectionModal}
        isOffline={!isOnline}
      />

      <SidebarProvider open={sidebarOpen} onOpenChange={setSidebarOpen}>
        {/* Mobile Analysis Menu Overlay */}
        {isMobile && mobileAnalysisMenuOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[9998]" onClick={() => setMobileAnalysisMenuOpen(false)} />
        )}

        {/* Mobile Analysis Menu Sidebar */}
        {isMobile && (
          <div className={`fixed left-0 top-0 h-full w-80 bg-background border-r border-border z-[9999] transform transition-transform duration-300 ease-in-out ${mobileAnalysisMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
            <div className="flex flex-col h-full">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div>
                    <h1 className="font-semibold text-foreground">{user?.first_name || user?.username}</h1>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {/* Hidden Archive button */}
                  <div className="hidden">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="p-1 h-8 w-8"
                      onClick={toggleArchivedAnalyses}
                      title={showArchivedAnalyses ? t('show_active_analyses') : t('show_archived_analyses')}
                    >
                      <Archive className="h-6 w-6" />
                    </Button>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="p-1 h-8 w-8">
                        <Settings className="h-6 w-6" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent 
                      align="start" 
                      side="bottom"
                      className="w-40 z-[9999] bg-popover border border-border shadow-lg"
                      sideOffset={2}
                      alignOffset={-20}
                    >
                      <DropdownMenuItem
                        onClick={toggleArchivedAnalyses}
                        className="cursor-pointer"
                      >
                        <Archive className="mr-2 h-4 w-4" />
                        {showArchivedAnalyses ? t('show_active_analyses') : t('show_archived_analyses')}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={handleConfiguration}
                        className="cursor-pointer"
                      >
                        <Cog className="mr-2 h-4 w-4" />
                        {t('configuration')}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={handleLogout}
                        className="cursor-pointer text-destructive focus:text-destructive"
                      >
                        <LogOut className="mr-2 h-4 w-4" />
                        {t('logout')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button variant="ghost" size="sm" className="p-1 h-8 w-8" onClick={() => setMobileAnalysisMenuOpen(false)}>
                    <X className="h-6 w-6" />
                  </Button>
                </div>
              </div>

              <div className="flex-1 overflow-hidden px-2">
                <AnalysisList 
                  analyses={allAnalyses} 
                  selectedId={analysisId ? parseInt(analysisId) : null} 
                  onSelect={handleAnalysisSelect} 
                  onNewAnalysis={handleNewAnalysis}
                  onRename={handleRenameAnalysis}
                  onDelete={handleDeleteAnalysis}
                  onArchive={handleArchiveAnalysis}
                  onRefresh={refreshAnalyses}
                  onLoadMore={fetchNextPage}
                  hasNextPage={hasNextPage}
                  isFetchingNextPage={isFetchingNextPage}
                  onCloseSidebar={handleMobileAnalysisMenuClose}
                  isLoading={analysesLoading || isFileUploadLoading}
                />
                {(isCreatingAnalysis || isCreatingAndNavigating || isFileUploadLoading) && (
                  <div className="px-4 py-2 text-sm text-muted-foreground flex items-center">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    {isFileUploadLoading ? t('synchronizing_analysis_list') : 
                     isCreatingAndNavigating ? t('creating_and_loading_analysis') : 
                     t('creating_analysis_loading')}
                  </div>
                )}
              </div>

              <div className="p-4 border-t border-border">
                <ConsultationDialog 
                  isRecording={isRecording}
                  showContinueButton={showContinueButton}
                  consultationDialogOpen={consultationDialogOpen}
                  microphoneErrorOpen={microphoneErrorOpen}
                  isLoading={templateLoading || !userTemplate}
                  isNewConsultation={isNewConsultation}
                  currentAnalysisId={analysisId ? parseInt(analysisId) : undefined}
                  onStartRecording={handleStartRecordingWithAnalysis}
                  onStopRecording={handleStopRecording}
                  onContinueConsultation={handleContinueConsultation}
                  onFinishConsultation={handleFinishConsultationWithPDF}
                  onUploadFile={handleUploadFile}
                  onOpenChange={setConsultationDialogOpen}
                  onMicrophoneErrorChange={setMicrophoneErrorOpen}
                  onRetryMicrophone={handleRetryMicrophone}
                  onFileTranscriptionComplete={handleFileTranscriptionCompleteWithRefresh}
                  template={userTemplate}
                />
              </div>
            </div>
          </div>
        )}

        {/* Desktop Sidebar */}
        {!isMobile && (
          <Sidebar className="border-r border-border">
            <SidebarHeader className="p-4 border-b border-border px-[15px] py-[18px]">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center space-x-3">
                  {selectedAnalysis && (
                    <Button variant="ghost" size="sm" onClick={toggleSidebar} className="p-1 h-8 w-8 cursor-pointer hover:bg-accent hover:text-accent-foreground">
                      <Menu className="h-5 w-5" />
                    </Button>
                  )}
                  <div className="group-data-[collapsible=icon]:hidden">
                    <h1 className="font-semibold text-foreground">{user?.first_name || user?.username}</h1>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {/* Hidden Archive button */}
                  <div className="hidden">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="p-1 h-8 w-8"
                      onClick={toggleArchivedAnalyses}
                      title={showArchivedAnalyses ? t('show_active_analyses') : t('show_archived_analyses')}
                    >
                      <Archive className="h-6 w-6" />
                    </Button>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="p-1 h-8 w-8">
                        <Settings className="h-6 w-6" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent 
                      align="start" 
                      side="bottom"
                      className="w-40 z-[9999] bg-popover border border-border shadow-lg"
                      sideOffset={2}
                      alignOffset={-20}
                    >
                      <DropdownMenuItem
                        onClick={toggleArchivedAnalyses}
                        className="cursor-pointer"
                      >
                        <Archive className="mr-2 h-4 w-4" />
                        {showArchivedAnalyses ? t('show_active_analyses') : t('show_archived_analyses')}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={handleConfiguration}
                        className="cursor-pointer"
                      >
                        <Cog className="mr-2 h-4 w-4" />
                        {t('configuration')}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={handleLogout}
                        className="cursor-pointer text-destructive focus:text-destructive"
                      >
                        <LogOut className="mr-2 h-4 w-4" />
                        {t('logout')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </SidebarHeader>

            <SidebarContent className="flex-1 overflow-hidden">
              <div className="analysis-selected h-full px-2">
                <AnalysisList 
                  analyses={allAnalyses} 
                  selectedId={analysisId ? parseInt(analysisId) : null} 
                  onSelect={handleAnalysisSelect} 
                  onNewAnalysis={handleNewAnalysis}
                  onRename={handleRenameAnalysis}
                  onDelete={handleDeleteAnalysis}
                  onArchive={handleArchiveAnalysis}
                  onRefresh={refreshAnalyses}
                  onLoadMore={fetchNextPage}
                  hasNextPage={hasNextPage}
                  isFetchingNextPage={isFetchingNextPage}
                  onCloseSidebar={handleMobileAnalysisMenuClose}
                  isLoading={analysesLoading || isFileUploadLoading}
                />
                {(isCreatingAnalysis || isCreatingAndNavigating || isFileUploadLoading) && (
                  <div className="px-4 py-2 text-sm text-muted-foreground flex items-center">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    {isFileUploadLoading ? t('synchronizing_analysis_list') : 
                     isCreatingAndNavigating ? t('creating_and_loading_analysis') : 
                     t('creating_analysis_loading')}
                  </div>
                )}
              </div>
            </SidebarContent>

            <SidebarFooter className="p-4 border-t border-border">
              <ConsultationDialog 
                isRecording={isRecording}
                showContinueButton={showContinueButton}
                consultationDialogOpen={consultationDialogOpen}
                microphoneErrorOpen={microphoneErrorOpen}
                isLoading={templateLoading || !userTemplate}
                isNewConsultation={isNewConsultation}
                currentAnalysisId={analysisId ? parseInt(analysisId) : undefined}
                onStartRecording={handleStartRecordingWithAnalysis}
                onStopRecording={handleStopRecording}
                onContinueConsultation={handleContinueConsultation}
                onFinishConsultation={handleFinishConsultationWithPDF}
                onUploadFile={handleUploadFile}
                onOpenChange={setConsultationDialogOpen}
                onMicrophoneErrorChange={setMicrophoneErrorOpen}
                onRetryMicrophone={handleRetryMicrophone}
                onFileTranscriptionComplete={handleFileTranscriptionCompleteWithRefresh}
                template={userTemplate}
              />
            </SidebarFooter>
          </Sidebar>
        )}

        {/* Main Content */}
        <main className="flex-1 flex flex-col overflow-hidden">
          {/* Mobile Header - Show when no analysis is selected */}
          {isMobile && !selectedAnalysis && (
            <div className="flex items-center justify-between p-4 border-b border-border bg-gray-900 py-[16px]">
              <h1 className="text-xl font-semibold text-slate-50">clara.care</h1>
              <Button variant="ghost" size="sm" onClick={handleMobileAnalysisMenuToggle} className="px-3 py-2 h-9 text-slate-50 hover:bg-accent hover:text-accent-foreground">
                <Menu className="h-5 w-5 mr-2" />
                Menu
              </Button>
            </div>
          )}

          {/* Top bar - hidden on mobile, when loading, creating, or uploading */}
          {selectedAnalysis && !isMobile && !isLoadingAnalysis && !isFileUploadLoading && !isCreatingAnalysis && !isCreatingAndNavigating && (
            <div className="hidden md:flex items-center p-4 border-b border-border bg-gray-900 py-[16px] flex-shrink-0">
              <div className="flex items-center space-x-4">
                {/* Menu button when sidebar is collapsed and analysis is selected */}
                {!sidebarOpen && <Button variant="ghost" size="sm" onClick={toggleSidebar} className="px-3 py-2 h-9 text-slate-50 hover:bg-accent hover:text-accent-foreground">
                    <Menu className="h-5 w-5 mr-2" />
                    Menu
                  </Button>}
                
              </div>
            </div>
          )}

          {/* Report Content */}
          <div className="flex-1 overflow-hidden relative">
            {(isLoadingAnalysis || isCreatingAndNavigating || isFileUploadLoading) ? (
              <div className="flex items-center justify-center h-full bg-background">
                <div className="text-center space-y-4">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
                  <div>
                    <h3 className="text-lg font-medium text-foreground">{t('loading')}</h3>
                    <p className="text-sm text-muted-foreground">
                      {isFileUploadLoading ? t('synchronizing_analysis_list') :
                       isCreatingAndNavigating ? t('creating_and_loading_analysis') : 
                       t('loading_analysis')}
                    </p>
                  </div>
                </div>
              </div>
            ) : selectedAnalysis ? (
              <div className="absolute inset-0">
                <ReportViewer 
                  analysis={selectedAnalysis.data} 
                  isLoading={analysisLoading}
                  isCreating={isCreatingAnalysis || isCreatingAndNavigating}
                  isFileUploading={isFileUploadLoading}
                  onMenuClick={handleMobileAnalysisMenuToggle}
                  isRecording={isRecording}
                  currentAnalysisId={selectedAnalysis.data.id}
                  transcriptions={combinedTranscriptions}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-full bg-background">
                <div className="text-center space-y-4">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
                  <div>
                    <h3 className="text-lg font-medium text-foreground">{t('loading')}</h3>
                    <p className="text-sm text-muted-foreground">{t('loading_analysis')}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>

        {/* Configuration Drawer */}
        <ConfigurationDrawer 
          open={configurationDrawerOpen} 
          onOpenChange={setConfigurationDrawerOpen} 
        />
      </SidebarProvider>

      {/* File Upload Modal */}
      <FileUploadModal
        open={fileUploadModalOpen}
        onOpenChange={setFileUploadModalOpen}
        onTranscriptionComplete={handleFileTranscriptionCompleteWithRefresh}
        template={userTemplate}
        onRefresh={refreshAnalyses}
      />

{tourVisible && (
        <Joyride
          run={tourVisible}
          callback={handleJoyrideCallback}
          locale={{
            back: t("back_to_login"),
            close: t("close"),
            last: t("finalize"),
            next: t("continue"),
            skip: "Omitir",
          }}
          showProgress={true}
          continuous={true}
          showSkipButton={true}
          styles={{
            options: {
              primaryColor: "black",
              backgroundColor: "#27c170",
              overlayColor: "rgba(0, 0, 0, 0.6)",
              beaconSize: 40,
              arrowColor: "#27c170",
              zIndex: 10000,
            }
          }}
          steps={[
            {
              target: ".analysis-selected",
              disableOverlay: false,
              disableBeacon: true,
              spotlightClicks: true,
              hideCloseButton: true,
              content: (
                <div className="bg-[#27c170] text-[black] rounded-lg font-xs text-left">
                  <h2 className="font-bold">{t("tour.title")}</h2>
                  <p>
                    <span>
                      {t("tour.description")} <br />
                      {t("tour.description_p1")}
                    </span>
                  </p>
                </div>
              ),
            },
          ]}
        />
      )}

      {/* Countdown Overlay - Outside SidebarProvider to ensure it's on top of everything */}
      <CountdownOverlay 
        key={countdownKey}
        isVisible={showCountdown} 
        onComplete={handleCountdownComplete} 
      />

      {/* 15-minute interval modal */}
      <ModalForceFinish
        isOpen={showIntervalModal}
        onClose={handleContinue}
        onFinalize={handleFinalize}
        countdown={60}
      />

      {/* Recording in progress modal */}
      <RecordingInProgressModal
        isOpen={modalRecordingInProcess}
        onClose={() => setModalRecordingInProcess(false)}
      />
    </>
  );
};

export default Dashboard;
